import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { finalize } from 'rxjs';

import type { Comment, Reply } from '@plugins/client/post/post-comments/types';
import {
  DEFAULT_COMMENT_PAGE_SIZE,
  DEFAULT_COMMENT_SORT,
} from '@plugins/newsfeed-lib/constants';
import type {
  CommentListDto,
  CreateCommentDto,
} from '@plugins/newsfeed-lib/types';
import type { Attachment } from '@plugins/shared/types';
import { SortType } from '@plugins/shared/types/sort';

/** The imported component hands its emitters plain callbacks. */
type Done = () => void;

/** Fields News Feed adds on top of what the imported component declares. */
interface Likeable {
  isLiked: boolean;
  likeCount: number;
}

export type ThreadComment = Comment & Likeable;
export type ThreadReply = Reply & Likeable;

/**
 * State of one post's comment thread.
 *
 * A plain signal-based service rather than NGXS: News Feed has no store
 * today, this is a single screen's state, and the component's emitters are
 * callback-shaped (`next` / `error`), which suits an imperative service.
 *
 * Not `providedIn: 'root'` — the thread belongs to the detail page, so it is
 * provided there and disposed with it, and switching posts starts clean.
 */
@Injectable()
export class CommentsStore {
  private readonly http = inject(HttpClient);

  private readonly postId = signal('');

  /**
   * Typed as the imported component's own `Comment` / `Reply`: `JsonDateModule`
   * has already turned the wire strings into `Date` by the time the response
   * reaches here, which is exactly what those types describe.
   */
  readonly comments = signal<ThreadComment[]>([]);
  /** The component takes replies keyed by parent id, not inlined. */
  readonly replies = signal<Record<string, ThreadReply[]>>({});
  readonly total = signal(0);
  readonly totalComments = signal(0);
  readonly sort = signal<SortType>(DEFAULT_COMMENT_SORT);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly error = signal(false);

  readonly allLoaded = computed(() => this.comments().length >= this.total());

  load(postId: string): void {
    this.postId.set(postId);
    this.#fetch(true);
  }

  loadMore(): void {
    if (this.allLoaded() || this.loadingMore()) return;

    this.#fetch(false);
  }

  /** A new sort is a fresh first page, never an append. */
  changeSort(sort: SortType): void {
    this.sort.set(sort);
    this.#fetch(true);
  }

  createComment(
    comment: { content: string; attachments?: Attachment[] },
    next: Done,
    error: Done,
  ): void {
    this.#write(
      this.http.post(
        `/api/posts/${this.postId()}/comments`,
        this.#body(comment),
      ),
      next,
      error,
    );
  }

  createReply(
    reply: { content: string; attachments?: Attachment[]; answerId: string },
    next: Done,
    error: Done,
  ): void {
    this.#write(
      this.http.post(
        `/api/comments/${reply.answerId}/replies`,
        this.#body(reply),
      ),
      next,
      error,
    );
  }

  editComment(
    comment: { id: string; content: string; attachments?: Attachment[] },
    next: Done,
    error: Done,
  ): void {
    this.#write(
      this.http.put(`/api/comments/${comment.id}`, this.#body(comment)),
      next,
      error,
    );
  }

  /**
   * `error` re-enables the row the component disabled; `disabledSetter` is
   * released by the refresh that follows a successful delete.
   */
  deleteComment(id: string, error: Done, disabledSetter: Done): void {
    this.#write(this.http.delete(`/api/comments/${id}`), disabledSetter, error);
  }

  /**
   * Desired state, never a toggle: a retried or double-tapped request must
   * not silently invert the like.
   */
  setLike(id: string, liked: boolean): void {
    const request = liked
      ? this.http.post(`/api/comments/${id}/like`, {})
      : this.http.delete(`/api/comments/${id}/like`);

    request.subscribe({
      next: () => this.#patchLike(id, liked),
      error: () => this.#fetch(true),
    });
  }

  toggleSubscription(id: string): void {
    this.http
      .post(`/api/comments/${id}/subscription`, {})
      .subscribe({ next: () => this.#fetch(true) });
  }

  #body(data: {
    content: string;
    attachments?: Attachment[];
  }): CreateCommentDto {
    return { content: data.content, attachments: data.attachments ?? [] };
  }

  /** Every write is followed by a refresh, so the thread stays authoritative. */
  #write(
    request: ReturnType<HttpClient['post']>,
    next: Done,
    error: Done,
  ): void {
    request.subscribe({
      next: () => {
        next();
        this.#fetch(true);
      },
      error: () => error(),
    });
  }

  #fetch(reset: boolean): void {
    const postId = this.postId();
    if (!postId) return;

    const skip = reset ? 0 : this.comments().length;

    this.error.set(false);
    (reset ? this.loading : this.loadingMore).set(true);

    this.http
      .get<CommentListDto>(`/api/posts/${postId}/comments`, {
        params: {
          skip,
          take: DEFAULT_COMMENT_PAGE_SIZE,
          sort: this.sort(),
        },
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.loadingMore.set(false);
        }),
      )
      .subscribe({
        next: (page) => this.#apply(page, reset),
        error: () => this.error.set(true),
      });
  }

  #apply(page: CommentListDto, reset: boolean): void {
    // `JsonDateModule` has already parsed the date strings, so the payload
    // matches the component's `Comment` shape apart from its declared types.
    const incoming = page.items as unknown as ThreadComment[];
    const items = reset ? incoming : [...this.comments(), ...incoming];

    this.comments.set(items);
    this.replies.set(
      items.reduce<Record<string, ThreadReply[]>>(
        (acc, comment) => ({
          ...acc,
          [comment.id]: (comment.comments ?? []) as ThreadReply[],
        }),
        {},
      ),
    );
    this.total.set(page.total);
    this.totalComments.set(page.totalComments);
  }

  /** Keeps the like optimistic so the whole thread does not have to reload. */
  #patchLike(id: string, liked: boolean): void {
    const patch = <T extends Likeable & { id: string }>(item: T): T =>
      item.id === id
        ? {
            ...item,
            isLiked: liked,
            likeCount: Math.max(0, item.likeCount + (liked ? 1 : -1)),
          }
        : item;

    this.comments.update((comments) =>
      comments.map((comment) => ({
        ...patch(comment),
        comments: ((comment.comments ?? []) as ThreadReply[]).map(patch),
      })),
    );
    this.replies.update((replies) =>
      Object.fromEntries(
        Object.entries(replies).map(([key, list]) => [key, list.map(patch)]),
      ),
    );
  }
}
